import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import VisibilityRounded from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRounded from '@mui/icons-material/VisibilityOffRounded';

export interface RevealTextProps {
  /** The value in full. Shown once the viewer asks for it. */
  value?: string | null;
  /**
   * How to shorten it while hidden. The default suits a phone number: the
   * country code and the last two digits, the rest as dots.
   */
  shorten?: (value: string) => string;
  /** What this value is, for the button's label: "phone number", "email". */
  what?: string;
  /** Shown when there is no value at all. */
  empty?: string;
  /** Matches the surrounding text; `caption` is the table-cell default. */
  variant?: 'caption' | 'body2' | 'body1';
}

/**
 * A value worth keeping off a shared screen until someone asks: shortened, with
 * an eye beside it that shows the whole thing.
 *
 * Hiding is a courtesy, not a protection — the full value is in the page, so
 * this guards against a shoulder, a screenshot or a projector, not against
 * anyone who wants it. That is the honest limit of the control, and the reason
 * it is worth having anyway: most of the time nobody needs the number, and a
 * list of them on a demo screen is nobody's business.
 *
 * Each one is revealed on its own and forgets on reload, because a reveal is an
 * answer to "what is this one", not a setting.
 */
export function RevealText({
  value,
  shorten = shortenPhone,
  what = 'number',
  empty = '—',
  variant = 'caption',
}: RevealTextProps) {
  const [shown, setShown] = React.useState(false);

  if (!value) {
    return (
      <Typography variant={variant} sx={(t) => ({ color: t.tokens.text.tertiary })}>
        {empty}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
      <Typography
        variant={variant}
        sx={(t) => ({
          color: t.tokens.text.tertiary,
          fontVariantNumeric: 'tabular-nums',
          overflowWrap: 'anywhere',
        })}
      >
        {shown ? value : shorten(value)}
      </Typography>
      <Tooltip title={shown ? `Hide ${what}` : `Show ${what}`}>
        <IconButton
          size="small"
          // These usually sit in a row that opens something when clicked. The
          // eye is its own action, not a way into the row, so the click stops
          // here rather than bubbling up and opening whatever is behind it.
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            setShown((was) => !was);
          }}
          aria-label={shown ? `Hide ${what}` : `Show ${what}`}
          aria-pressed={shown}
          sx={(t) => ({
            p: 0.25,
            color: t.tokens.text.tertiary,
            '&:hover': { color: t.tokens.text.secondary },
          })}
        >
          {shown
            ? <VisibilityOffRounded sx={{ fontSize: 14 }} />
            : <VisibilityRounded sx={{ fontSize: 14 }} />}
        </IconButton>
      </Tooltip>
    </Box>
  );
}

/** "+917875053067" -> "+91 ••••••••67". Keeps the country code and last two. */
export function shortenPhone(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length < 6) return value;
  const code = digits.startsWith('1') ? '1' : digits.slice(0, 2);
  const hidden = digits.length - code.length - 2;
  return `+${code} ${'•'.repeat(Math.max(0, hidden))}${digits.slice(-2)}`;
}
